import React, { useEffect, useState } from 'react';
import { grid } from '@noths/polaris-client-ribbons-base';
import {
  addParamsToHref,
  getDeliveryByEventLabel,
  getProductIdFromProductCode,
  isMiddleMouseClick,
  linkClickOpensInNewTab,
} from '@noths/polaris-client-utils';

import { InListingCallout } from 'src/components/molecules/InListingCallout/InListingCallout';
import { ProductCard } from 'src/components/organisms/ProductList/components/ProductCard/ProductCard';
import type { ProductListProps } from 'src/components/organisms/ProductList/types';
import { PRODUCT_CARD_GRID_ITEM_ID } from 'src/components/organisms/ProductPages/constants/elementIds';
import type { Product } from 'src/redux/products/types/Product';
import { PRODUCT_ID_DATA_ATTRIBUTE } from 'src/utils/scrollProductCardIntoView';
import { getDeliveryLabel } from '../../utils/getDeliveryLabel';
import { getPrimaryLabel } from '../../utils/getPrimaryLabel';
import * as styles from './ProductList.styles';

const INITIAL_NUMBER_OF_PRODUCTS_TO_DISABLE_LAZY_LOADING = 6;
export const PRODUCT_CARD_GRID_ITEM_TEST_ID = 'PRODUCT_CARD_GRID_ITEM_TEST_ID';
/**
 * Currently responsible for rendering:
 *  - lists of products
 *  - lists of expandedProducts (if expanded search scenario)
 *  - expanded search messaging
 *
 * Should be responsible for rendering:
 *  - a list of products (agnostic about whether it's a full page, exact results, expanded search results)
 * @component
 */
export const ProductList = ({
  currency,
  expandedProducts,
  expandedResultsMessage,
  favouriteProductCodes = [],
  inListingsCallout,
  onInListingsCalloutClick,
  onProductClick,
  onProductVisible,
  page,
  products,
  providerAttributionToken,
  referredBy,
  updateFavourite,
  updatePageHistory,
}: ProductListProps) => {
  const [isClient, setIsClient] = useState(false);
  const [fadeInProductList] = useState(isClient);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Convert favouriteProductCodes to Set for O(1) lookup
  const favouriteProductCodesSet = new Set(favouriteProductCodes);

  // Create tracking params
  const trackingParams = {
    ...(providerAttributionToken && { attributionToken: providerAttributionToken }),
    ...(referredBy && { referredBy }),
  };

  const handleProductVisible = ({ productCode }: { productCode: number }) => {
    onProductVisible({ productCode });
  };

  const handleProductCardClick =
    (mousedown: boolean) =>
    (evt: React.MouseEvent<HTMLAnchorElement>, { productCode }: { productCode: number }) => {
      const opensInNewTab = linkClickOpensInNewTab(evt) || isMiddleMouseClick(evt);

      if (mousedown && !isMiddleMouseClick(evt)) {
        return;
      }

      if (!opensInNewTab) {
        evt.preventDefault();
      } else {
        updatePageHistory({ page, productCode });
      }

      const hrefWithTrackingParams = addParamsToHref(evt.currentTarget.href, trackingParams);

      onProductClick({
        productUrl: hrefWithTrackingParams,
        destinationUrl: evt.currentTarget.href,
        productCode,
        linkOpensInSameWindow: !opensInNewTab,
      });
    };

  const renderProducts = (products: Product[]) => {
    return products.map((product, index) => {
      const deliveryEventMessage = product.deliveryByEvent?.arriveByEvent
        ? getDeliveryByEventLabel(product.deliveryByEvent.eventName)
        : undefined;
      const deliveryLabelText = product.purchasable
        ? getDeliveryLabel(product.deliveryDays, product.deliveryCost, currency)
        : '';
      const imageLoading =
        index < INITIAL_NUMBER_OF_PRODUCTS_TO_DISABLE_LAZY_LOADING ? 'eager' : 'lazy';
      const primaryLabel = getPrimaryLabel(
        product.isNew,
        product.purchasable,
        product.salePercentage || 0,
      );

      return (
        <div
          css={styles.productCardGridItem}
          data-testid={PRODUCT_CARD_GRID_ITEM_TEST_ID}
          id={`${PRODUCT_CARD_GRID_ITEM_ID}-${index}`}
          key={`${product.code}`}
          {...{ [PRODUCT_ID_DATA_ATTRIBUTE]: getProductIdFromProductCode(product.code) }}
        >
          <ProductCard
            code={product.code}
            currency={currency}
            deliveryEventMessage={deliveryEventMessage}
            deliveryLabelText={deliveryLabelText.trim()}
            favourite={favouriteProductCodesSet.has(String(product.code))}
            href={product.detailPageHref}
            imageLoading={imageLoading}
            imageSrc={product.imageSrc}
            onFavouriteClick={updateFavourite}
            onProductClick={handleProductCardClick(false)}
            onProductMouseDown={handleProductCardClick(true)}
            onVisible={handleProductVisible}
            partnerName={product.partner.name}
            preSalePrice={
              product.preSalePrices?.find((price) => price.currency === currency)?.amount
            }
            price={product.price.amount}
            title={product.title}
            {...(primaryLabel
              ? {
                  primaryLabelText: primaryLabel.text,
                  primaryLabelType: primaryLabel.type,
                }
              : {})}
          />
        </div>
      );
    });
  };

  return (
    <div css={[grid, styles.productListWrapper, fadeInProductList && styles.fadeIn]}>
      {products && renderProducts(products)}
      {inListingsCallout && (
        <aside css={styles.inListingCalloutWrapper}>
          <InListingCallout
            ctaHref={inListingsCallout.ctaHref}
            ctaText={inListingsCallout.ctaText}
            heading={inListingsCallout.heading}
            imgSrc={inListingsCallout.imgUrl}
            onClick={onInListingsCalloutClick}
          />
        </aside>
      )}
      {expandedResultsMessage && expandedResultsMessage}
      {expandedProducts && renderProducts(expandedProducts)}
    </div>
  );
};
